import React from 'react';
import useSEO from "./useSEO";

const HealthcareStockAudit = () => {
  useSEO({
    title: "Health Care Stock Audit | Hospital Inventory Audit Services in Hyderabad | Medical Equipment Verification Experts",
    description:
      "Ensure accuracy, compliance, and efficiency with professional healthcare stock audit services in Hyderabad. We specialize in auditing hospital inventories, medical equipment, consumables, and pharmaceuticals to enhance control and reduce costs.",
    keywords:
      "healthcare stock audit, hospital inventory audit, medical equipment audit, healthcare audit Hyderabad, pharmacy stock audit, clinic inventory verification, hospital consumables audit, healthcare asset verification, medical supplies audit, healthcare compliance audit, hospital stock reconciliation, healthcare management audit, hospital stock verification",
    ogTitle: "Health Care Stock Audit - Hyderabad Experts",
    ogDescription:
      "Comprehensive healthcare stock audits for hospitals and clinics — ensuring accuracy, compliance, and better cost management.",
    ogImage: "img/healthcare-stock-audit.png",
    twitterTitle: "Health Care Stock Audit - Hyderabad Experts",
    twitterDescription:
      "Professional hospital and healthcare stock audit services. Improve accuracy, reduce wastage, and ensure compliance across medical inventories.",
    twitterImage: "img/healthcare-stock-audit.png"
  });

  return (
    <div>
      <>
        {/* Section 1 - Hero */}
        <section className="pt-lg-8 pt-xl-9">
          <div className="container position-relative pt-4 pt-lg-0">
            <div className="row align-items-center">
              {/* Text Content */}
              <div className="col-md-7 col-xl-6 mb-7 mb-md-0">
                <h1 className="mb-4 text-primary">Health Care Stock Audit</h1>
                <p className="mb-4">
                  A <b>Health Care Stock Audit</b> ensures that hospitals, clinics,
                  and medical institutions maintain accurate inventory levels,
                  reduce wastage, and meet compliance standards. Our audits cover
                  pharmaceuticals, consumables, surgical instruments, and medical
                  equipment to strengthen operational efficiency and transparency.
                </p>
                <p>
                  <b>Why Healthcare Inventory Auditing Matters:</b><br />
                  In the healthcare sector, even minor stock discrepancies can
                  disrupt patient care or lead to compliance issues. Our audit
                  services provide complete visibility into inventory movement,
                  helping management make data-driven decisions.
                </p>
              </div>

              {/* Image */}
              <div className="col-md-5 ms-auto text-center">
                <img
                  src="imgs/health-care-stock-audit.jpg"
                  alt="Health Care Stock Audit"
                  className="rounded-3 shadow-sm w-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - Audit Process */}
        <section className="overflow-hidden py-5 bg-light border-top">
          <div className="container">
            <div className="row align-items-center">
              {/* Image */}
              <div className="col-lg-6 text-center mb-5 mb-lg-0">
                <div className="pe-lg-6 pe-xl-8">
                  <img
                    src="imgs/health-care-stock-audit-process.jpg"
                    alt="Hospital Audit Process"
                    className="rounded-4 shadow-sm w-100"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="col-lg-6 ms-auto">
                <h2 className="mb-lg-4">Our Health Care Audit Process</h2>
                <hr className="my-4 my-lg-5" />

                <ul className="list-group list-group-borderless">
                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Medical Equipment Verification</b>
                  </li>
                  <p>
                    Physical verification of medical devices and equipment to
                    ensure they are properly recorded, functional, and compliant
                    with safety standards.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Pharmaceutical & Consumables Audit</b>
                  </li>
                  <p>
                    Verification of medicines, disposables, and consumable items
                    for expiry, usage, and reconciliation with pharmacy records.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Departmental Stock Reconciliation</b>
                  </li>
                  <p>
                    Ensuring accurate inventory across departments like pharmacy,
                    laboratory, operation theatre, ICU, and general wards.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Asset & Equipment Tracking</b>
                  </li>
                  <p>
                    Tagging and verifying high-value hospital assets using barcode
                    or RFID for accountability and insurance compliance.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Inventory Valuation & Compliance Review</b>
                  </li>
                  <p>
                    Validating the valuation of medical supplies and ensuring
                    compliance with NABH, NABL, and internal audit policies.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Waste & Expiry Management Audit</b>
                  </li>
                  <p>
                    Identifying expired medicines, unused consumables, or slow-
                    moving inventory to reduce waste and optimize stock levels.
                  </p>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3 - Benefits */}
        <section className="py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="text-primary">Benefits of Health Care Stock Audit</h2>
              <p className="lead">
                Empower your hospital or clinic with accurate inventory control,
                cost efficiency, and full regulatory compliance.
              </p>
            </div>

            <div className="row text-center">
              <div className="col-md-4 mb-4">
                <i className="bi bi-clipboard-data text-success fs-3 mb-2"></i>
                <h5>Regulatory Compliance</h5>
                <p>
                  Meet NABH, NABL, and Drug Control Department requirements with
                  accurate stock documentation and reporting.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-box-seam text-success fs-3 mb-2"></i>
                <h5>Reduced Wastage</h5>
                <p>
                  Identify and eliminate expired or unused inventory to reduce
                  losses and free up working capital.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-graph-up text-success fs-3 mb-2"></i>
                <h5>Operational Efficiency</h5>
                <p>
                  Ensure each department has the right stock at the right time,
                  improving patient care and hospital management.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-cash-stack text-success fs-3 mb-2"></i>
                <h5>Cost Optimization</h5>
                <p>
                  Avoid overstocking and minimize unnecessary procurement
                  expenses with proper inventory visibility.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-shield-check text-success fs-3 mb-2"></i>
                <h5>Asset Protection</h5>
                <p>
                  Prevent misuse or loss of high-value medical equipment through
                  tagging and regular audits.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-lightbulb text-success fs-3 mb-2"></i>
                <h5>Data Transparency</h5>
                <p>
                  Improve management insights with real-time, data-driven audit
                  reports and dashboards.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    </div>
  );
};

export default HealthcareStockAudit;
